/**
 * LogsPage - Epic 38 - Story 38.1
 *
 * Página principal do sistema de Logs de Auditoria
 * com filtros multi-select auto-apply, date range, multi-column sorting, paginação e export XLSX
 */

import React, { useState, useEffect, useMemo } from 'react';
import { MainLayout } from '../components/MainLayout';
import { LogsDataTable } from '../components/LogsDataTable';
import { LogDetailsModal } from '../components/LogDetailsModal';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { Download, RefreshCw, Search } from 'lucide-react';
import logService from '../services/logService';
import type {
  AuditLog,
  AuditLogListItem,
  AuditLogFilters,
  MultiSortConfig,
  OperationOption,
  EntityCount,
} from '../types/auditLog';

const LogsPage: React.FC = () => {
  // Estados individuais para filtros (padrão CalendarioPage)
  const [selectedOperacoes, setSelectedOperacoes] = useState<string[]>([]);
  const [selectedEntidades, setSelectedEntidades] = useState<string[]>([]);
  const [usuarioSearch, setUsuarioSearch] = useState('');
  const [selectedOrigem, setSelectedOrigem] = useState<string>(''); // S27.3 - Filtro Mobile/Web

  // Estados de dados
  const [logs, setLogs] = useState<AuditLogListItem[]>([]);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [sortConfig, setSortConfig] = useState<MultiSortConfig>([
    { key: 'data_hora', direction: 'desc' }, // Default: mais recente primeiro
  ]);
  const [operations, setOperations] = useState<OperationOption[]>([]);
  const [entities, setEntities] = useState<EntityCount[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(50);

  // Construir objeto de filtros a partir dos estados individuais (auto-apply)
  const filters: AuditLogFilters = useMemo(() => {
    const f: AuditLogFilters = {};
    if (selectedOperacoes.length > 0) f.operacao = selectedOperacoes as any;
    if (selectedEntidades.length > 0) f.entidade = selectedEntidades;
    if (usuarioSearch.trim()) f.usuario_search = usuarioSearch.trim();
    if (selectedOrigem) f.origem = selectedOrigem as any;
    return f;
  }, [selectedOperacoes, selectedEntidades, usuarioSearch, selectedOrigem]);

  /**
   * Carregar logs com filtros e paginação
   */
  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await logService.listAuditLogs(
        filters,
        sortConfig,
        currentPage,
        pageSize
      );

      setLogs(response.results);
      setTotalPages(response.total_pages);
      setTotalCount(response.count);
    } catch (err: any) {
      console.error('Erro ao carregar logs:', err);
      setError(err.response?.data?.detail || err.response?.data?.message || 'Erro ao carregar logs de auditoria');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Carregar metadados (operações e entidades)
   */
  const loadMetadata = async () => {
    try {
      const [ops, ents] = await Promise.all([
        logService.getOperations(),
        logService.getEntities(),
      ]);

      setOperations(ops);
      setEntities(ents);
    } catch (err: any) {
      console.error('Erro ao carregar metadados:', err);
    }
  };

  /**
   * Carregar detalhes de um log
   */
  const handleLogClick = async (log: AuditLogListItem) => {
    try {
      const fullLog = await logService.getAuditLog(log.id);
      setSelectedLog(fullLog);
      setShowDetailsModal(true);
    } catch (err: any) {
      console.error('Erro ao carregar detalhes do log:', err);
      setError(err.response?.data?.detail || 'Erro ao carregar detalhes do log');
    }
  };

  /**
   * Exportar para XLSX
   */
  const handleExport = async () => {
    try {
      setExporting(true);
      await logService.exportXlsx(filters, sortConfig, 10000);
    } catch (err: any) {
      console.error('Erro ao exportar logs:', err);
      setError('Erro ao exportar logs para XLSX');
    } finally {
      setExporting(false);
    }
  };

  /**
   * Limpar todos os filtros
   */
  const handleClearFilters = () => {
    setSelectedOperacoes([]);
    setSelectedEntidades([]);
    setUsuarioSearch('');
    setSelectedOrigem('');
    setCurrentPage(1);
  };

  /**
   * Atualizar ordenação
   */
  const handleSortChange = (newSortConfig: MultiSortConfig) => {
    setSortConfig(newSortConfig);
    setCurrentPage(1); // Reset para página 1 ao ordenar
  };

  /**
   * Atualizar página
   */
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Carregar metadados na montagem
  useEffect(() => {
    loadMetadata();
  }, []);

  // Recarregar logs quando filtros, ordenação ou página mudarem (auto-apply)
  useEffect(() => {
    loadLogs();
  }, [filters, sortConfig, currentPage]);

  // Preparar options para MultiSelectFilter
  const operacaoOptions = operations.map(op => ({
    value: op.value,
    label: op.label,
  }));

  const entidadeOptions = entities.map(ent => ({
    value: ent.entidade,
    label: `${ent.entidade} (${ent.count})`,
  }));

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <MainLayout>
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Logs do Sistema
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {totalCount} registros encontrados
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Refresh */}
              <button
                onClick={loadLogs}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                Atualizar
              </button>

              {/* Export XLSX */}
              <button
                onClick={handleExport}
                disabled={exporting || logs.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={16} />
                {exporting ? 'Exportando...' : 'Exportar XLSX'}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {/* Filtros (padrão CalendarioPage - auto-apply) */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Operação */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Operação
                </label>
                <MultiSelectFilter
                  label=""
                  options={operacaoOptions}
                  selectedValues={selectedOperacoes}
                  onChange={setSelectedOperacoes}
                  placeholder="Todas as operações"
                  showTodos={true}
                  showBadges={false}
                />
              </div>

              {/* Entidade */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Entidade
                </label>
                <MultiSelectFilter
                  label=""
                  options={entidadeOptions}
                  selectedValues={selectedEntidades}
                  onChange={setSelectedEntidades}
                  placeholder="Todas as entidades"
                  showTodos={true}
                  showBadges={false}
                />
              </div>

              {/* S27.3 - Origem (Web/Mobile) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Origem
                </label>
                <select
                  value={selectedOrigem}
                  onChange={(e) => setSelectedOrigem(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Todas as origens</option>
                  <option value="WEB">🌐 Web</option>
                  <option value="MOBILE">📱 Mobile</option>
                </select>
              </div>

              {/* Usuário */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Usuário
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search size={16} className="text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="text"
                    value={usuarioSearch}
                    onChange={(e) => setUsuarioSearch(e.target.value)}
                    placeholder="Nome, email, username ou ID"
                    className="w-full pl-10 pr-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Botão Limpar */}
              <div className="flex items-end">
                <button
                  onClick={handleClearFilters}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>
          </div>

          {/* DataTable */}
          <LogsDataTable
            logs={logs}
            sortConfig={sortConfig}
            onSortChange={handleSortChange}
            onLogClick={handleLogClick}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            loading={loading}
          />

          {/* Details Modal */}
          <LogDetailsModal
            log={selectedLog}
            isOpen={showDetailsModal}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedLog(null);
            }}
          />
        </div>
      </MainLayout>
    </div>
  );
};

export default LogsPage;
